import { useEffect, useState } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { ACTAS_ROSTER } from '../data/roster-config'
import { rosterShift } from '../data/roster-shifts'
import { addDays, addMonths, daysInMonth, parseIsoDate, startOfMonth } from '../engine/civil'
import { effectiveRoster } from '../engine/effective-roster'
import type { RecurrenceRule } from '../engine/recurrence'
import type { DayException, IsoDate, ShiftCode, ShiftInstance } from '../engine/types'
import { createAdvertisement, fetchMyAdvertisements, withdrawAdvertisement } from '../lib/ads'
import type { AdCallResult, AdIntent, MyAdvertisement } from '../lib/ads'
import { formatShortDate, formatTimeRange, MONTH_LONG, WEEKDAY_SHORT } from '../lib/format-date'
import {
  addFwaShift,
  deleteException,
  deleteFwaShift,
  deleteRecurrence,
  ensureRecurrenceCoverage,
  fetchRosterData,
  saveRecurrence,
  upsertException,
} from '../lib/roster-data'
import type { AddFwaShiftResult, RosterData } from '../lib/roster-data'
import { useSession } from '../lib/session-context'
import { AdvertiseSheet } from '../components/AdvertiseSheet'
import type { AdvertiseTarget } from '../components/AdvertiseSheet'
import { DaySheet } from '../components/DaySheet'
import { MonthGrid } from '../components/MonthGrid'
import { RecurrenceSheet } from '../components/RecurrenceSheet'
import { ExceptionDayRow } from '../components/ExceptionDayRow'
import { RosterShiftRow } from '../components/RosterShiftRow'
import { Button, EmptyState, SegmentedControl, ShiftList } from '../ui'

type View = 'month' | 'agenda'

export interface CalendarAgendaProps {
  shifts: readonly ShiftInstance[]
  /**
   * Exception days — leave, unavailable, not working — interleaved with the
   * shifts in date order, each as its own `ExceptionDayRow`. Optional only so
   * the Phase 1 callers that pass no exceptions still typecheck; the screen
   * passes the real rows through.
   */
  exceptions?: readonly DayException[]
  /**
   * Days carrying an OPEN advertisement — their shift rows render the
   * "Advertised" badge (Phase 4).
   */
  advertisedDates?: readonly IsoDate[]
  /**
   * Replaces the default "No shifts this month" copy — the screen hands the
   * FWA sentence down, because "add a shift" is advice only an FWA user can
   * act on from here.
   */
  emptyTitle?: string
  emptyBody?: string
}

/**
 * The Agenda view's body, split out from `CalendarScreen` so it renders
 * (and can be tested) on its own, without going through the
 * `SegmentedControl`'s interactive state — this app's static-markup tests
 * render through `renderToStaticMarkup`, which never processes a click.
 *
 * Shift rows and exception rows interleave in date order (ISO strings sort
 * chronologically), shifts before an exception on the same date — an
 * exception suppresses the *derived* shift, but an explicit shift can
 * legitimately share a date with one (`LEAVE_OVER_EXPLICIT`), and the
 * working shift is the earlier-reading of the two. The exception is never
 * rendered as a shift: `ExceptionDayRow` is its own component on purpose.
 *
 * `ShiftList`'s own title is the generic "Shifts" rather than the month
 * name: the nav heading above already names the month in both views (the
 * same reasoning `MonthGrid` visually hides its own `<caption>` for), so
 * repeating it here would stack two identical headings on screen.
 *
 * Rows stay read-only — no tap handler. The grid's day buttons are the one
 * tap surface, in both views.
 */
export function CalendarAgenda({
  shifts,
  exceptions = [],
  advertisedDates = [],
  emptyTitle = 'No shifts this month',
  emptyBody = 'Nothing from your block falls in this month — try Previous or Next to look at another one.',
}: CalendarAgendaProps) {
  const advertisedDaySet = new Set<IsoDate>(advertisedDates)
  const rows = [
    ...shifts.map((shift) => ({ kind: 'shift' as const, date: shift.date, shift })),
    ...exceptions.map((exception) => ({
      kind: 'exception' as const,
      date: exception.date,
      exception,
    })),
  ].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1
    return a.kind === b.kind ? 0 : a.kind === 'shift' ? -1 : 1
  })

  return (
    <ShiftList title="Shifts" count={rows.length}>
      {rows.length === 0 ? (
        <EmptyState title={emptyTitle} body={emptyBody} />
      ) : (
        rows.map((row) =>
          row.kind === 'shift' ? (
            <RosterShiftRow
              key={`shift-${row.shift.date}-${row.shift.code}`}
              date={formatShortDate(row.shift.date)}
              code={row.shift.code}
              timeRange={formatTimeRange(
                rosterShift(row.shift.code).startMin,
                rosterShift(row.shift.code).endMin,
              )}
              advertised={advertisedDaySet.has(row.shift.date)}
            />
          ) : (
            <ExceptionDayRow
              key={`exception-${row.exception.date}-${row.exception.kind}`}
              date={formatShortDate(row.exception.date)}
              kind={row.exception.kind}
              note={row.exception.note}
            />
          ),
        )
      )}
    </ShiftList>
  )
}

export interface CalendarScreenProps {
  /**
   * Today, injected rather than read from the clock — the same seam
   * `MonthGrid` runs, so the screen stays deterministic in tests. Everything
   * else the screen needs (block, roster type, week start, user id) comes
   * from `useSession()`'s profile.
   */
  today: IsoDate
}

/**
 * Month grid and agenda (PLAN.md §8) — the first screen that shows a
 * paramedic their *actual* roster: the block pattern, their leave and
 * unavailable days, and every FWA shift and recurrence they have entered,
 * merged by `effectiveRoster()`'s five ordered steps.
 *
 * **One code path.** There is no branch on `roster_type` anywhere in the
 * computation — `block` (null for FWA) and the fetched exceptions and
 * explicit rows go into the same `effectiveRoster` call for every staff
 * type, which is the phase's done criterion 4. `roster_type` is read in
 * exactly two places, both affordances the plan explicitly assigns to this
 * screen: the FWA entry points / empty-state copy, and `DaySheet`'s
 * `canAddFwaShift`.
 *
 * **Data.** On mount and on every visible-month change:
 * `ensureRecurrenceCoverage` (lazy extension — a month beyond the rules'
 * generated rows is filled in before it is shown) then `fetchRosterData`,
 * whose `{ data, error }` decides what renders. Loading renders a named
 * state, never an empty one: the empty states below render only once the
 * fetch for *this* month has resolved — tracked by `loadedMonth`, so a slow
 * next-month fetch cannot show the previous month's rows under the new
 * heading, which is R5's lie from the other side.
 *
 * **Horizon (A7).** The day sheet opens only for dates within
 * `horizonRange(today)` — three months back, six forward, from
 * `ACTAS_ROSTER.horizon`. A tap outside does nothing destructive; it says
 * so, naming the date tapped and the range that is editable.
 */
export function CalendarScreen({ today }: CalendarScreenProps) {
  const { profile } = useSession()
  const [view, setView] = useState<View>('month')
  const [visibleMonth, setVisibleMonth] = useState<IsoDate>(today)
  const [data, setData] = useState<RosterData | null>(null)
  /** The user's own advertisements — the badge in grid and agenda, the ad in the day sheet. */
  const [ads, setAds] = useState<MyAdvertisement[]>([])
  /** The month `data` belongs to — what stops a stale month rendering under a new heading. */
  const [loadedMonth, setLoadedMonth] = useState<IsoDate | null>(null)
  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading')
  const [loadError, setLoadError] = useState<PostgrestError | null>(null)
  const [daySheetDate, setDaySheetDate] = useState<IsoDate | null>(null)
  /** The create-path recurrence sheet's start date; `null` until opened. */
  const [recurrenceSheetDate, setRecurrenceSheetDate] = useState<IsoDate | null>(null)
  /** The rule being edited, for the recurrence sheet's edit path. */
  const [editRule, setEditRule] = useState<RecurrenceRule | null>(null)
  /** The shift the advertise composer is open for; `null` until opened. */
  const [advertiseTarget, setAdvertiseTarget] = useState<AdvertiseTarget | null>(null)
  const [horizonNote, setHorizonNote] = useState<IsoDate | null>(null)
  /** Bumped after every successful mutation, and by Retry — re-runs the load below. */
  const [reloadNonce, setReloadNonce] = useState(0)

  const block = profile?.block_code ?? null
  const rosterType = profile?.roster_type ?? null
  const weekStart = profile?.week_start ?? 'SUNDAY'
  const userId = profile?.id ?? null
  // Affordance read #1 — FWA is also the only type with a null block
  // post-gate, but the roster_type check is what the plan names, and
  // `block === null` alone would show entry points to a half-switched
  // profile. Affordance only; the computation below never reads it.
  const isFwa = block === null && rosterType === 'FWA'
  // Affordance read #2 — who gets the add-shift section inside DaySheet.
  const canAddFwaShift = rosterType === 'FWA' || rosterType === 'BLOCK_PARTIAL'

  const monthStart = startOfMonth(visibleMonth)
  const { year, month: monthNum } = parseIsoDate(monthStart)
  const monthEnd = addDays(monthStart, daysInMonth(year, monthNum) - 1)
  const monthRange = { from: monthStart, to: monthEnd }
  // A7's bound, computed from the injected `today` via ACTAS_ROSTER's own
  // horizon — the same figures `horizonRange`'s engine-side twin would use.
  const horizon = {
    from: addMonths(today, -ACTAS_ROSTER.horizon.monthsBack),
    to: addMonths(today, ACTAS_ROSTER.horizon.monthsForward),
  }

  const monthIsCurrent = data !== null && loadedMonth === monthStart
  const monthShifts =
    data !== null && loadedMonth === monthStart
      ? effectiveRoster(
          { block, range: monthRange, exceptions: data.exceptions, explicit: data.explicit },
          ACTAS_ROSTER,
        )
      : []

  useEffect(() => {
    if (userId === null) return
    const uid: string = userId
    let cancelled = false
    async function load() {
      // Lazy extension first, then one fetch — coverage's inserts are part
      // of what the fetch should see (plan §1, "Lazy extension").
      setPhase('loading')
      const coverage = await ensureRecurrenceCoverage(uid, monthRange, today)
      if (cancelled) return
      if (coverage.error !== null) {
        setPhase('error')
        setLoadError(coverage.error)
        return
      }
      const result = await fetchRosterData(uid, monthRange)
      if (cancelled) return
      if (result.data === null) {
        setPhase('error')
        setLoadError(result.error)
        return
      }
      // Own advertisements alongside roster data — no new code path, just a
      // second parallel query (plan §1 Client, the CalendarScreen row).
      // Ads are fetched unfiltered by range: a badge belongs to the shift
      // wherever the month is scrolled, exactly like a recurrence rule.
      const adResult = await fetchMyAdvertisements(uid)
      if (cancelled) return
      if (adResult.data === null) {
        setPhase('error')
        setLoadError(adResult.error)
        return
      }
      setData(result.data)
      setAds(adResult.data)
      setLoadedMonth(monthStart)
      setPhase('ready')
    }
    void load()
    return () => {
      cancelled = true
    }
    // monthRange is rebuilt each render; its two endpoints are the identity
    // that matters, and both are plain strings.
  }, [userId, monthStart, monthEnd, today, reloadNonce])

  // Defensive fallback, consistent with `isProfileComplete`: post-gate this
  // cannot render — the shell only mounts once the gate says READY — but a
  // missing profile, a missing roster type, or a null block on a non-FWA
  // type is exactly the state the gate would bounce, so the screen says so
  // rather than deriving from a block it does not have.
  if (profile === null || rosterType === null || (block === null && rosterType !== 'FWA')) {
    return (
      <div className="sl-stack">
        <EmptyState
          title="Choose your roster block to see your calendar"
          body="Once you've told us which block you're on, this fills in with your next few weeks — every AM, D, PM and N your roster gives you, worked out from the block pattern."
        />
      </div>
    )
  }

  // Narrowed alias for the handlers below — above this line the profile is
  // real, so its id is too, and the alias keeps every closure honest
  // without a single assertion.
  const uid: string = profile.id

  function refetch() {
    setReloadNonce((nonce) => nonce + 1)
  }

  /** Flags a refetch when a write succeeded; passes the error through untouched. */
  function afterWrite(error: PostgrestError | null): PostgrestError | null {
    if (error === null) refetch()
    return error
  }

  function handleDayTap(date: IsoDate) {
    if (date < horizon.from || date > horizon.to) {
      // Inert, but never a mystery — the date that was tapped and the
      // editable range are both named (CLAUDE.md: no unexplained no-op).
      setHorizonNote(date)
      return
    }
    setHorizonNote(null)
    setDaySheetDate(date)
  }

  /** The day the FWA entry points open on: today when it is visible, else the month's first day, clamped into the horizon. */
  function entryDate(): IsoDate {
    const candidate = today >= monthStart && today <= monthEnd ? today : monthStart
    if (candidate < horizon.from) return horizon.from
    if (candidate > horizon.to) return horizon.to
    return candidate
  }

  function openDaySheet(date: IsoDate) {
    setHorizonNote(null)
    setRecurrenceSheetDate(null)
    setEditRule(null)
    setDaySheetDate(date)
  }

  function openRecurrenceSheet(startDate: IsoDate) {
    setHorizonNote(null)
    setDaySheetDate(null)
    setEditRule(null)
    setRecurrenceSheetDate(startDate)
  }

  function openRecurrenceEditor(rule: RecurrenceRule) {
    setHorizonNote(null)
    setDaySheetDate(null)
    setRecurrenceSheetDate(null)
    setEditRule(rule)
  }

  async function handleAddFwaShift(date: IsoDate, code: ShiftCode): Promise<AddFwaShiftResult> {
    const result = await addFwaShift(uid, { date, code })
    // A duplicate wrote nothing — refetching would be a lie about what changed.
    if (!result.duplicate) afterWrite(result.error)
    return result
  }

  async function handleDeleteFwaShift(shiftId: string) {
    const { error } = await deleteFwaShift(uid, shiftId)
    return { error: afterWrite(error) }
  }

  async function handleMarkException(
    date: IsoDate,
    kind: DayException['kind'],
    note: string | null,
  ) {
    const { error } = await upsertException(uid, { date, kind, note })
    return { error: afterWrite(error) }
  }

  async function handleClearException(exceptionId: string | undefined) {
    if (exceptionId === undefined) return { error: null }
    const { error } = await deleteException(uid, exceptionId)
    return { error: afterWrite(error) }
  }

  async function handleSaveRecurrence(rule: RecurrenceRule) {
    const result = await saveRecurrence(uid, rule, today)
    if (result.error === null && result.guard === null) refetch()
    return result
  }

  async function handleDeleteRecurrence(rule: RecurrenceRule) {
    // The editor only opens for a fetched rule, which always has an id; the
    // guard keeps the type honest rather than asserting it.
    if (rule.id === undefined) return { guard: null, error: null }
    const result = await deleteRecurrence(uid, rule.id)
    // The sheet renders the outcome; the list behind it refetches on success.
    if (result.error === null && result.guard === null) refetch()
    return result
  }

  /**
   * Submits the advertise composer for the open target. The explicit path
   * names the row's id; the materialise path names date + code and lets
   * `create_advertisement` (0013) verify the caller actually works it and
   * materialise the BLOCK_MATERIALISED row. Refetch on success only — the
   * sheet renders the typed result either way.
   */
  async function handleAdvertise(draft: {
    noNightSwap: boolean
    intent: AdIntent
    note: string | null
  }): Promise<AdCallResult> {
    if (advertiseTarget === null) {
      return { ok: false, rejection: 'BAD_ARGS', error: null }
    }
    const result = await createAdvertisement({
      shiftId: advertiseTarget.shiftId,
      workDate: advertiseTarget.shiftId === undefined ? advertiseTarget.date : undefined,
      shiftCode: advertiseTarget.shiftId === undefined ? advertiseTarget.code : undefined,
      noNightSwap: draft.noNightSwap,
      intent: draft.intent,
      note: draft.note,
    })
    if (result.ok) refetch()
    return result
  }

  async function handleWithdraw(ad: MyAdvertisement): Promise<AdCallResult> {
    const result = await withdrawAdvertisement(ad.id)
    if (result.ok) refetch()
    return result
  }

  const daySheetException =
    daySheetDate === null
      ? null
      : (data?.exceptions.find((candidate) => candidate.date === daySheetDate) ?? null)
  const daySheetShifts =
    daySheetDate === null ? [] : monthShifts.filter((shift) => shift.date === daySheetDate)
  const daySheetAds =
    daySheetDate === null ? [] : ads.filter((ad) => ad.date === daySheetDate)
  const advertisedDates = Array.from(
    new Set(ads.filter((ad) => ad.status === 'OPEN').map((ad) => ad.date)),
  )

  const heading = `${MONTH_LONG[monthNum - 1]} ${year}`

  return (
    <div className="sl-stack">
      <div className="ss-calendar-nav">
        <Button
          variant="secondary"
          aria-label="Previous month"
          onClick={() => {
            setVisibleMonth((current) => addMonths(current, -1))
            // The note names a date under the *old* heading; under the new one
            // it is a stale sentence about a month no longer on screen.
            setHorizonNote(null)
          }}
        >
          Previous
        </Button>
        <h2 className="sl-heading">{heading}</h2>
        <Button
          variant="secondary"
          aria-label="Next month"
          onClick={() => {
            setVisibleMonth((current) => addMonths(current, 1))
            setHorizonNote(null)
          }}
        >
          Next
        </Button>
      </div>

      <SegmentedControl
        label="View"
        options={[
          { value: 'month', label: 'Month' },
          { value: 'agenda', label: 'Agenda' },
        ]}
        value={view}
        onChange={setView}
        size="compact"
      />

      {horizonNote !== null ? (
        <p className="sl-hint" role="status">
          {formatShortDate(horizonNote)} is outside the range you can edit — days from{' '}
          {formatShortDate(horizon.from)} to {formatShortDate(horizon.to)} can be marked or
          changed.
        </p>
      ) : null}

      {/* FWA entry points — buttons above the grid, per plan §1. Both are
          FWA-only: a BLOCK_PARTIAL user adds shifts through the DaySheet a
          day tap opens, and a BLOCK user derives everything. */}
      {isFwa ? (
        <div className="ss-calendar-actions">
          <Button onClick={() => openDaySheet(entryDate())}>Add shift</Button>
          <Button variant="secondary" onClick={() => openRecurrenceSheet(entryDate())}>
            Add recurrence
          </Button>
        </div>
      ) : null}

      {/* The FWA empty state lives with the entry points: "add your first
          shift", never "choose your block", because for an FWA user there is
          nothing to choose — the roster is whatever they enter. It renders
          only once this month's fetch has resolved, so a loading calendar
          never flashes it (R5). */}
      {isFwa && monthIsCurrent && monthShifts.length === 0 ? (
        <EmptyState
          title="Add your first shift"
          body="Your calendar is whatever you enter — a one-off shift, or a rule that repeats. Use the buttons above, or tap any day."
        />
      ) : null}

      {/* The user's recurrence rules, with the edit/delete entry the plan's
          browser-pass step 4 needs ("Edit the recurrence's shift code").
          Rules are fetched unfiltered by range, so the list is the same in
          every month. */}
      {isFwa && monthIsCurrent && data !== null && data.recurrences.length > 0 ? (
        <div className="ss-recurrence-list">
          <span className="sl-label">Repeating shifts</span>
          {data.recurrences.map((rule) => (
            <div key={rule.id} className="ss-recurrence-list__row">
              <span className="ss-recurrence-list__sentence">{recurrenceSentence(rule)}</span>
              <Button
                variant="ghost"
                aria-label={`Edit the rule: ${recurrenceSentence(rule)}`}
                onClick={() => openRecurrenceEditor(rule)}
              >
                Edit
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {phase === 'error' ? (
        <>
          <EmptyState
            title="Couldn't load your calendar"
            body={
              loadError !== null
                ? `The calendar couldn't be loaded — ${loadError.message}. Check your connection and try again.`
                : "The calendar couldn't be loaded. Check your connection and try again."
            }
          />
          <Button onClick={refetch}>Try again</Button>
        </>
      ) : !monthIsCurrent ? (
        // Loading, named as loading — never an empty state, which would be a
        // lie about a roster nobody has fetched yet (R5).
        <p className="sl-hint" role="status">
          Loading your roster…
        </p>
      ) : data === null ? null : view === 'month' ? (
        <MonthGrid
          month={monthStart}
          shifts={monthShifts}
          exceptions={data.exceptions}
          onDayTap={handleDayTap}
          today={today}
          weekStart={weekStart}
          advertisedDates={advertisedDates}
        />
      ) : (
        <CalendarAgenda
          shifts={monthShifts}
          exceptions={data.exceptions}
          advertisedDates={advertisedDates}
          emptyBody={
            isFwa
              ? 'Nothing you have entered falls in this month — try Previous or Next, or add a shift with the buttons above.'
              : 'Nothing from your block falls in this month — try Previous or Next to look at another one.'
          }
        />
      )}

      {/* Days are tappable, and the Phase 1 "view-only" half of this line is
          long gone; Phase 4 retires the "coming later" half too. Month view
          only — in agenda there is no grid, so the hint would promise a tap
          that does nothing. */}
      {view === 'month' ? (
        <p className="sl-hint">
          Tap any day to mark it — leave, unavailable, not working — to add or remove a shift, or
          to offer one of its shifts for swap.
        </p>
      ) : null}

      {daySheetDate !== null ? (
        <DaySheet
          key={daySheetDate}
          date={daySheetDate}
          shifts={daySheetShifts}
          ads={daySheetAds}
          exception={daySheetException}
          canAddFwaShift={canAddFwaShift}
          onAddFwaShift={(code) => handleAddFwaShift(daySheetDate, code)}
          onDeleteFwaShift={(shiftId) => handleDeleteFwaShift(shiftId)}
          onAdvertise={setAdvertiseTarget}
          onWithdraw={handleWithdraw}
          onMarkException={(kind, note) => handleMarkException(daySheetDate, kind, note)}
          onClearException={() => handleClearException(daySheetException?.id)}
          onClose={() => setDaySheetDate(null)}
        />
      ) : null}

      {advertiseTarget !== null ? (
        <AdvertiseSheet
          key={`${advertiseTarget.date}-${advertiseTarget.code}-${advertiseTarget.shiftId ?? 'derived'}`}
          target={advertiseTarget}
          onAdvertise={handleAdvertise}
          onClose={() => setAdvertiseTarget(null)}
        />
      ) : null}

      {recurrenceSheetDate !== null ? (
        <RecurrenceSheet
          key={`create-${recurrenceSheetDate}`}
          rule={null}
          defaultStartDate={recurrenceSheetDate}
          onSave={handleSaveRecurrence}
          today={today}
          onClose={() => setRecurrenceSheetDate(null)}
        />
      ) : null}

      {editRule !== null ? (
        <RecurrenceSheet
          key={`edit-${editRule.id}`}
          rule={editRule}
          onSave={handleSaveRecurrence}
          onDelete={() => handleDeleteRecurrence(editRule)}
          today={today}
          onClose={() => setEditRule(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * A recurrence rule as one sentence for the "Repeating shifts" list — the
 * same words the sheet's own controls use, so editing back reads as the
 * same rule. `weekday` is Sunday-first, `WEEKDAY_SHORT`'s own convention.
 */
function recurrenceSentence(rule: RecurrenceRule): string {
  const cadence =
    rule.kind === 'INTERVAL_DAYS'
      ? `Every ${rule.intervalDays} days`
      : rule.weeksInterval === 1
        ? `Weekly on ${WEEKDAY_SHORT[rule.weekday ?? 0]}`
        : `Every ${rule.weeksInterval} weeks on ${WEEKDAY_SHORT[rule.weekday ?? 0]}`
  return `${cadence} — ${rule.shiftCode}, from ${rule.startDate}`
}
